import { useState, useEffect, useRef } from "react";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";
import { useMcpSchema } from "@/hooks/useMcpSchema";
import { suggestTableByFileName } from "@/lib/tableMatcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Table2, Sparkles } from "lucide-react";

interface SyncTablePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileCount: number;
  fileNames?: string[];
  onConfirm: (tableName: string) => void;
  confirming?: boolean;
}

const SyncTablePickerDialog = ({ open, onOpenChange, fileCount, fileNames, onConfirm, confirming }: SyncTablePickerDialogProps) => {
  const { toast } = useToast();
  const { tables, isLoading: schemaLoading, refetch } = useMcpSchema();

  const [selectedTable, setSelectedTable] = useState<string>("");
  const [showNewTable, setShowNewTable] = useState(false);
  const [newTableName, setNewTableName] = useState("");
  const [creatingTable, setCreatingTable] = useState(false);
  const [suggestedTable, setSuggestedTable] = useState<string | null>(null);
  const suggestRanRef = useRef(false);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  // Auto-suggest table based on file names
  useEffect(() => {
    if (!open || schemaLoading || tables.length === 0 || suggestRanRef.current) return;
    if (!fileNames || fileNames.length === 0) return;

    suggestRanRef.current = true;

    // Try matching the first file name
    for (const name of fileNames) {
      const match = suggestTableByFileName(name, tables);
      if (match) {
        setSuggestedTable(match);
        setSelectedTable(match);
        return;
      }
    }
  }, [open, schemaLoading, tables, fileNames]);

  const handleCreateTable = async () => {
    if (!newTableName.trim()) {
      toast({ title: "Error", description: "Table name is required.", variant: "destructive" });
      return;
    }

    setCreatingTable(true);
    try {
      const headers = await buildEdgeFunctionHeaders();
      const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/createSchema`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          table_name: newTableName.trim(),
          columns: [{ name: "ida", type: "string" }],
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to create table" }));
        throw new Error(err.error || err.detail || `Request failed (${res.status})`);
      }

      toast({ title: "Table created", description: `"${newTableName.trim()}" created successfully.` });
      setSelectedTable(newTableName.trim());
      setNewTableName("");
      setShowNewTable(false);
      setSuggestedTable(null);
      await refetch();
    } catch (err: any) {
      toast({ title: "Create failed", description: err.message, variant: "destructive" });
    }
    setCreatingTable(false);
  };

  const resetState = () => {
    setSelectedTable("");
    setShowNewTable(false);
    setNewTableName("");
    setSuggestedTable(null);
    suggestRanRef.current = false;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) resetState();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Table2 className="h-5 w-5" />
            Choose Target Table
          </DialogTitle>
          <DialogDescription>
            Select a table to sync {fileCount} selected file{fileCount !== 1 ? "s" : ""} into.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Target Table</Label>
            {schemaLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading tables…
              </div>
            ) : (
              <>
                <Select value={selectedTable} onValueChange={(v) => { setSelectedTable(v); setSuggestedTable(null); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a table" />
                  </SelectTrigger>
                  <SelectContent>
                    {tables.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {suggestedTable && selectedTable === suggestedTable && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Sparkles className="h-3 w-3 text-amber-500" />
                    Suggested: "{suggestedTable}" — file name match
                  </p>
                )}
              </>
            )}
          </div>

          {!showNewTable ? (
            <Button variant="outline" size="sm" className="gap-1" onClick={() => setShowNewTable(true)}>
              <Plus className="h-3.5 w-3.5" />
              Create New Table
            </Button>
          ) : (
            <div className="space-y-2 rounded-md border p-3">
              <Label>New Table Name</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. sales_data"
                  value={newTableName}
                  onChange={(e) => setNewTableName(e.target.value)}
                  disabled={creatingTable}
                />
                <Button onClick={handleCreateTable} disabled={creatingTable || !newTableName.trim()} size="sm">
                  {creatingTable ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowNewTable(false)} className="text-xs">
                Cancel
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={confirming}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(selectedTable)} disabled={!selectedTable || confirming}>
            {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Approve & Sync
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SyncTablePickerDialog;
